/* eslint-disable import/order */
import { useQuery } from '@tanstack/react-query';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, ButtonGroup, Col, Form, Row, ToggleButton } from 'react-bootstrap';
import 'react-range-slider-input/dist/style.css';
import { Link, useNavigate } from 'react-router-dom';
import { ClearanceStatusEnum, Provider, ProviderService, UserDetail } from '../../../../api';
import { Card } from '../../../../app/components/Card';
import { ClearanceBadge } from '../../../../app/components/ClearanceBadge';
import { Pagination } from '../../../../app/components/Pagination/Pagination';
import { RangeSlider } from '../../../../app/components/RangeSlider/RangeSlider';
import { useSearchParamsState } from '../../../../app/hooks/search-params';
import { providerQueryKeys } from '../../../../app/query-keys-factories/providers';
import { CERTIFICATES_STATES } from '../../../../utils/certificates-local-data';
import { useCurrencyFormatter } from '../../../../app/hooks/use-currency-formatter';
import { useForm, useWatch, Control } from 'react-hook-form';
import { SelectController } from '../../../../app/components/SelectController';
import { components, ControlProps } from 'react-select';
import { BsCheck, BsSortDown, BsSortUp } from 'react-icons/bs';
import { FilterDropdown } from '../../../../app/components/FilterDropDown';
import { useCurrentUser } from '../../../../app/hooks/auth';
import styles from './ProviderList.module.scss';

const PAGE_SIZE = 20;
const NOT_CONNECTED = 'not_connected' as const;

const getColumns = (
  myProvider: boolean,
  formatCurrency: (value: number | string | undefined | null) => string,
  user: UserDetail | undefined
): ColumnDef<Provider>[] => {
  const canCreateConnection = user?.permissions?.includes('create__connections__connectedfacility');

  const baseColumns: ColumnDef<Provider>[] = [
    {
      header: 'Name',
      accessorKey: 'display_name',
      cell: (props) => {
        const provider = props.row.original;

        if (canCreateConnection) {
          return (
            <span>
              <Link to={`/app/healthcare-entity/providers/${provider.id}`}>
                {props.getValue<string>()}
              </Link>
            </span>
          );
        }

        if (provider.availability_token) {
          return (
            <span>
              <Link to={`/pub/provider/availability/${provider.availability_token}/`}>
                {props.getValue<string>()}
              </Link>
            </span>
          );
        }

        return <span>{props.getValue<string>()}</span>;
      },
      size: 20,
      minSize: 10,
    },
    {
      header: 'Status',
      accessorKey: 'clearance_status',
      cell: (props) => <ClearanceBadge status={props.getValue() as ClearanceStatusEnum} />,
      size: 10,
      minSize: 0,
    },
    {
      header: 'Specialties',
      accessorFn: (row): string => row.specialties.join(', '),
      cell: (props) => <div className={`{styles.lineClamp} small`}>{props.getValue<string>()}</div>,
      size: 13,
      minSize: 0,
    },
    // Only include hourly rate column if not myProvider
    ...(!myProvider
      ? [
          {
            id: 'hourly_rate',
            header: () => <div className="text-end">Hourly rate</div>,
            accessorFn: (row) => formatCurrency(row.marketplace_hourly_fee),
            cell: (props) => <div className="text-end">{props.getValue()}</div>,
            size: 10,
            minSize: 0,
          },
        ]
      : []),
    {
      header: 'Licensed to work in',
      accessorKey: 'medical_licensed_states',
      cell: (props) => (
        <Badges
          items={props.getValue<string[]>().map((key) => CERTIFICATES_STATES[key])}
          providerId={props.row.original.id}
        />
      ),
      size: 18,
      minSize: 0,
    },
    {
      header: 'IMLC',
      accessorKey: 'has_imlc',
      cell: (props) => props.getValue() && <BsCheck className="text-color-primary" size="20px" />,
      size: 5,
      minSize: 0,
    },
  ];

  return baseColumns;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ProviderList = () => {
  const navigate = useNavigate();
  const [currentPageStr, setCurrentPage] = useSearchParamsState('page', '1');
  const [myProviderFilter, setMyProviderFilter] = useSearchParamsState('my_provider', '1');
  const { formatCurrency } = useCurrencyFormatter();
  const { user } = useCurrentUser();
  const [hasImlcFilter, setHasImlcFilter] = useSearchParamsState('has_imlc', '0');
  const [displayNameFilter, setPreferredNameFilter] = useSearchParamsState(
    'display_name__icontains',
    ''
  );
  const [hourlyRateGteFilter, setHourlyRateGteFilter] = useSearchParamsState(
    'marketplace_hourly_fee__gte',
    '0'
  );
  const [hourlyRateLteFilter, setHourlyRateLteFilter] = useSearchParamsState(
    'marketplace_hourly_fee__lte',
    '1000'
  );

  const [specialtiesOverlapFilter, setSpecialtiesOverlapFilter] = useSearchParamsState(
    'specialties__overlap',
    [],
    true
  );
  const [licensedStatesOverlapFilter, setLicensedStatesOverlapFilter] = useSearchParamsState(
    'licensed_states__overlap',
    [],
    true
  );
  const [clearanceStatusFilter, setClearanceStatusFilter] = useSearchParamsState(
    'clearance_statuses',
    [],
    true
  );
  const [certificationsTypeOverlapFilter, setCertificationsOverlapFilter] = useSearchParamsState(
    'certifications__overlap',
    [],
    true
  );
  const [boardCertificationsTypeOverlapFilter, setBoardCertificationsOverlapFilter] =
    useSearchParamsState('board_certifications__overlap', [], true);
  const [schedulesDateGteFilter, setSchedulesDateGteFilter] = useSearchParamsState(
    'schedules__start_on__gte',
    ''
  );
  const [schedulesDateLteFilter, setSchedulesDateLteFilter] = useSearchParamsState(
    'schedules__start_on__lte',
    ''
  );
  const [orderFilter, setOrderFilter] = useSearchParamsState('order', 'display_name');

  const setNameFilter = useCallback(
    debounce(
      (value) => {
        setPreferredNameFilter(value);
      },
      1000,
      { trailing: true }
    ),
    []
  );

  const setRateFilters = useCallback(
    debounce(
      (value) => {
        if (value[0] !== parseInt(hourlyRateGteFilter))
          setHourlyRateGteFilter(value?.[0]?.toString());
        if (value[1] !== parseInt(hourlyRateLteFilter))
          setHourlyRateLteFilter(value?.[1]?.toString());
      },
      1000,
      { trailing: true }
    ),
    [hourlyRateGteFilter, hourlyRateLteFilter]
  );
  const currentPage = parseInt(currentPageStr);
  const params = {
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
    myProvider: myProviderFilter === '1',
    displayNameIcontains: displayNameFilter || undefined,
    marketplaceHourlyFeeGte: hourlyRateGteFilter || undefined,
    marketplaceHourlyFeeLte: hourlyRateLteFilter || undefined,
    specialtiesOverlap: specialtiesOverlapFilter?.length > 0 ? specialtiesOverlapFilter : undefined,
    licensedStatesOverlap:
      licensedStatesOverlapFilter?.length > 0 ? licensedStatesOverlapFilter : undefined,
    hasImlc: hasImlcFilter === '1',
    clearanceStatuses: clearanceStatusFilter?.length > 0 ? clearanceStatusFilter : undefined,
    certificationsTypeOverlap:
      certificationsTypeOverlapFilter?.length > 0 ? certificationsTypeOverlapFilter : undefined,
    boardCertificationsTypeOverlap:
      boardCertificationsTypeOverlapFilter?.length > 0
        ? boardCertificationsTypeOverlapFilter
        : undefined,
    schedulesStartOnGte: schedulesDateGteFilter || undefined,
    schedulesStartOnLte: schedulesDateLteFilter || undefined,
    order: orderFilter || undefined,
  };

  const queryProviders = useQuery({
    queryKey: providerQueryKeys.list(params as Parameters<typeof ProviderService.providerList>[0]),
    queryFn: async () => {
      return await ProviderService.providerList(
        params as Parameters<typeof ProviderService.providerList>[0]
      );
    },
    keepPreviousData: true,
  });

  const [hourlyRateGteFilterState, setHourlyRateGteFilterState] = useState(
    parseInt(hourlyRateGteFilter)
  );
  const [hourlyRateLteFilterState, setHourlyRateLteFilterState] = useState(
    parseInt(hourlyRateLteFilter)
  );
  const [displayNameFilterState, setPreferredNameFilterState] = useState('');

  const statusesOptionDict = useMemo(() => {
    if (myProviderFilter === '1') {
      return {
        [ClearanceStatusEnum.CLEARED]: window.CLEARANCE_STATUS[ClearanceStatusEnum.CLEARED],
        [ClearanceStatusEnum.PENDING_CLEARANCE]:
          window.CLEARANCE_STATUS[ClearanceStatusEnum.PENDING_CLEARANCE],
      };
    }

    return {
      [NOT_CONNECTED]: 'Not Connected',
      ...window.CLEARANCE_STATUS,
    };
  }, [myProviderFilter]);

  const table = useReactTable({
    data: queryProviders?.data?.results ?? [],
    columns: getColumns(myProviderFilter === '1', formatCurrency, user),
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    setRateFilters([hourlyRateGteFilterState, hourlyRateLteFilterState]);
  }, [hourlyRateGteFilterState, hourlyRateLteFilterState]);
  useEffect(() => {
    setNameFilter(displayNameFilterState);
  }, [displayNameFilterState]);

  const handleChangePage = (page: number) => {
    setCurrentPage(page.toString());
  };

  const renderItems = () => {
    if (queryProviders.isLoading || queryProviders.isFetching) {
      return <SkeletonTableRows />;
    }

    if (queryProviders.isError) {
      return (
        <TableRow>
          <td colSpan={8} className="text-center">
            Error
          </td>
        </TableRow>
      );
    }

    if (queryProviders.data?.count === 0) {
      return (
        <TableRow>
          <td colSpan={8} className="text-center">
            No providers found
          </td>
        </TableRow>
      );
    }

    const canCreateConnection = user?.permissions?.includes(
      'create__connections__connectedfacility'
    );

    return table.getRowModel().rows.map((row) => {
      const provider = row.original;
      let onClick: (() => void) | undefined = () =>
        navigate(`/app/healthcare-entity/providers/${provider.id}`);
      let className = 'hover-bg-light';

      if (!canCreateConnection) {
        if (provider.availability_token) {
          onClick = () => navigate(`/pub/provider/availability/${provider.availability_token}/`);
        } else {
          onClick = undefined;
          className = '';
        }
      }

      return (
        <TableRow key={row.id} onClick={onClick} className={className}>
          {row.getVisibleCells().map((cell) => (
            <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
          ))}
        </TableRow>
      );
    });
  };

  const { control, getValues, setValue, reset } = useForm({
    defaultValues: {
      order: orderFilter,
      sortDirection: 'asc',
      specialties: specialtiesOverlapFilter || [],
      licensed_states: licensedStatesOverlapFilter || [],
      clearance_statuses: clearanceStatusFilter || [],
      certifications: certificationsTypeOverlapFilter || [],
      board_certifications: boardCertificationsTypeOverlapFilter || [],
      hasImlc: hasImlcFilter === '1',
    },
  });

  const sortOptions = [
    { value: 'display_name', label: 'Name' },
    { value: 'marketplace_hourly_fee', label: 'Hourly rate' },
  ];

  const changeSortDirection = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const sortDirection = getValues().sortDirection === 'asc' ? 'desc' : 'asc';
    setValue('sortDirection', sortDirection);
    const currentOrder = getValues().order;

    if (currentOrder) {
      setOrderFilter(sortDirection === 'desc' ? `-${currentOrder}` : currentOrder);
    }
  };

  const specialtiesOptions = Object.keys(window.SPECIALTIES).map((key) => ({
    value: key,
    label: window.SPECIALTIES[key],
  }));

  const filtersFormValues = useWatch({
    control,
  });

  // Update tempFilters state
  const [tempFilters, setTempFilters] = useState({
    specialties: specialtiesOverlapFilter || [],
    licensed_states: licensedStatesOverlapFilter || [],
    clearance_statuses: clearanceStatusFilter || [],
    certifications: certificationsTypeOverlapFilter || [],
    board_certifications: boardCertificationsTypeOverlapFilter || [],
    hourlyRateGte: hourlyRateGteFilterState,
    hourlyRateLte: hourlyRateLteFilterState,
    schedulesStartOnGte: schedulesDateGteFilter || '',
    schedulesStartOnLte: schedulesDateLteFilter || '',
    hasImlc: hasImlcFilter === '1',
  });

  // Update filtersCount
  const filtersCount = useMemo(() => {
    const values = filtersFormValues;
    let count = 0;

    // Count array filters
    Object.keys(values).forEach((key) => {
      if (Array.isArray(values[key]) && values[key].filter((i) => i).length > 0) {
        count++;
      }
    });

    // Check hourly rate changes
    if (tempFilters.hourlyRateGte !== 0 || tempFilters.hourlyRateLte !== 1000) {
      count++;
    }

    // Count date range filters
    if (tempFilters.schedulesStartOnGte || tempFilters.schedulesStartOnLte) {
      count++;
    }

    // Count IMLC filter if it's active
    if (tempFilters.hasImlc === '1' || (hasImlcFilter === '1' && tempFilters.hasImlc === '0')) {
      count++;
    }

    return count;
  }, [filtersFormValues, tempFilters, hasImlcFilter]);

  // Update onApply to reset the modified state
  const onApply = () => {
    setSpecialtiesOverlapFilter(tempFilters.specialties);
    setLicensedStatesOverlapFilter(tempFilters.licensed_states);
    setClearanceStatusFilter(tempFilters.clearance_statuses);
    setCertificationsOverlapFilter(tempFilters.certifications);
    setBoardCertificationsOverlapFilter(tempFilters.board_certifications);
    setHourlyRateGteFilter(tempFilters.hourlyRateGte.toString());
    setHourlyRateLteFilter(tempFilters.hourlyRateLte.toString());
    setSchedulesDateGteFilter(tempFilters.schedulesStartOnGte);
    setSchedulesDateLteFilter(tempFilters.schedulesStartOnLte);
    setHasImlcFilter(tempFilters.hasImlc);
    setCurrentPage('1');
  };

  // Update clearFilters to handle the IMLC modified state
  const clearFilters = () => {
    reset({
      ...getValues(),
      specialties: [],
      licensed_states: [],
      clearance_statuses: [],
      certifications: [],
      board_certifications: [],
      hasImlc: false,
    });
    setTempFilters((prev) => ({
      ...prev,
      specialties: [],
      licensed_states: [],
      clearance_statuses: [],
      certifications: [],
      board_certifications: [],
      hourlyRateGte: 0,
      hourlyRateLte: 1000,
      schedulesStartOnGte: '',
      schedulesStartOnLte: '',
      hasImlc: '0',
    }));
    setSpecialtiesOverlapFilter([]);
    setLicensedStatesOverlapFilter([]);
    setClearanceStatusFilter([]);
    setCertificationsOverlapFilter([]);
    setBoardCertificationsOverlapFilter([]);
    setHourlyRateGteFilter('0');
    setHourlyRateLteFilter('1000');
    setSchedulesDateGteFilter('');
    setSchedulesDateLteFilter('');
    setHasImlcFilter('0');
    setCurrentPage('1');
  };

  // Update onToggle to properly reset states
  const onToggle = () => {
    // Reset form values to current filter state when dropdown toggles
    reset({
      ...getValues(),
      specialties: specialtiesOverlapFilter || [],
      licensed_states: licensedStatesOverlapFilter || [],
      clearance_statuses: clearanceStatusFilter || [],
      certifications: certificationsTypeOverlapFilter || [],
      board_certifications: boardCertificationsTypeOverlapFilter || [],
    });
    // Reset temporary filters to match current applied filters
    setTempFilters((prev) => ({
      ...prev,
      specialties: specialtiesOverlapFilter || [],
      licensed_states: licensedStatesOverlapFilter || [],
      clearance_statuses: clearanceStatusFilter || [],
      certifications: certificationsTypeOverlapFilter || [],
      board_certifications: boardCertificationsTypeOverlapFilter || [],
      hasImlc: hasImlcFilter,
    }));
  };

  const certificationsOptions = useMemo(() => {
    if (!window.CERTIFICATION_TYPES) return [];
    return Object.keys(window.CERTIFICATION_TYPES).map((key) => ({
      value: key,
      label: window.CERTIFICATION_TYPES[key],
    }));
  }, []);

  const boardCertificationsOptions = useMemo(() => {
    if (!window.BOARD_CERTIFICATION_TYPES) return [];
    return Object.keys(window.BOARD_CERTIFICATION_TYPES).map((key) => ({
      value: key,
      label: window.BOARD_CERTIFICATION_TYPES[key],
    }));
  }, []);

  return (
    <>
      <Row className="justify-content-center" xs={'auto'}>
        <Col>
          <Alert variant="danger" show={queryProviders.isError}>
            <Alert.Heading>Something went wrong</Alert.Heading>
            <p>Please try again later. If the problem persists, please contact us.</p>
          </Alert>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col className="mb-4 mb-lg-0">
          <ButtonGroup className="providers-type-filter primary-lite-toggle">
            <ToggleButton
              value="1"
              type="radio"
              name="cleared"
              variant="outline-primary"
              checked={myProviderFilter === '1'}
              onClick={() => {
                setMyProviderFilter('1');
                setCurrentPage('1');
              }}
              onKeyUp={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  setMyProviderFilter('1');
                  setCurrentPage('1');
                }
              }}
            >
              My Providers
            </ToggleButton>
            <ToggleButton
              value="0"
              type="radio"
              name="marketplace"
              variant="outline-primary"
              checked={myProviderFilter === '0'}
              onClick={() => {
                setMyProviderFilter('0');
                setCurrentPage('1');
              }}
              onKeyUp={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  setMyProviderFilter('0');
                  setCurrentPage('1');
                }
              }}
            >
              Find a Provider
            </ToggleButton>
          </ButtonGroup>
        </Col>
        <Col xs={12} md="auto">
          <Row className="justify-content-start align-items-center">
            <Col xs={12} md="auto" className="mb-1 mb-md-0 pe-md-1">
              <Form.Control
                type="text"
                placeholder="Search by name"
                value={displayNameFilterState}
                onChange={(e) => {
                  setPreferredNameFilterState(e.target.value);
                  setCurrentPage('1');
                }}
              />
            </Col>
            <Col xs="auto" className="px-md-1 pe-1">
              <div className={styles.providerListFilters}>
                <FilterDropdown
                  isDirty={!!filtersCount}
                  filtersCount={filtersCount}
                  onClear={clearFilters}
                  onApply={onApply}
                  onToggle={onToggle}
                >
                  <Form.Group className="mb-3">
                    <Form.Label className="text-filter-form-label mb-2 ms-2">
                      Hourly Rate Range
                    </Form.Label>
                    <div className="px-2">
                      <RangeSlider
                        value={[tempFilters.hourlyRateGte, tempFilters.hourlyRateLte]}
                        onInput={([min, max]) => {
                          setTempFilters((prev) => ({
                            ...prev,
                            hourlyRateGte: min,
                            hourlyRateLte: max,
                          }));
                        }}
                        min={0}
                        max={1000}
                        step={1}
                      />
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3 mx-2">
                    <Form.Label className="text-filter-form-label" htmlFor="specialties">
                      Specialties
                    </Form.Label>
                    <SelectController
                      menuPortalTarget={
                        document.getElementById('filter-dropdown-portal') as HTMLElement
                      }
                      control={control}
                      name="specialties"
                      placeholder=""
                      options={specialtiesOptions}
                      onChange={(selection: any) => {
                        const values = Array.isArray(selection)
                          ? selection.map((option) => option.value)
                          : [];
                        setTempFilters((prev) => ({
                          ...prev,
                          specialties: values,
                        }));
                      }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 mx-2">
                    <Form.Label className="text-filter-form-label" htmlFor="licensed_states">
                      Licensed States
                    </Form.Label>
                    <SelectController
                      menuPortalTarget={
                        document.getElementById('filter-dropdown-portal') as HTMLElement
                      }
                      control={control}
                      name="licensed_states"
                      placeholder=""
                      options={Object.keys(CERTIFICATES_STATES).map((key) => ({
                        value: key,
                        label: CERTIFICATES_STATES[key],
                      }))}
                      onChange={(selection: any) => {
                        const values = Array.isArray(selection)
                          ? selection.map((option) => option.value)
                          : [];
                        setTempFilters((prev) => ({
                          ...prev,
                          licensed_states: values,
                        }));
                      }}
                    />
                  </Form.Group>

                  <Form.Group className="mx-2">
                    <Form.Check
                      type="checkbox"
                      id="has_imlc"
                      label="Holds a Compact License (IMLC)"
                      className="text-filter-form-label form-label ms-4 mb-2"
                      checked={tempFilters.hasImlc === '1'}
                      onChange={(e) => {
                        const newValue = e.target.checked ? '1' : '0';
                        setTempFilters((prev) => ({
                          ...prev,
                          hasImlc: newValue,
                        }));
                      }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 mx-2">
                    <Form.Label className="text-filter-form-label" htmlFor="clearance_statuses">
                      Clearance Status
                    </Form.Label>
                    <SelectController
                      menuPortalTarget={
                        document.getElementById('filter-dropdown-portal') as HTMLElement
                      }
                      control={control}
                      name="clearance_statuses"
                      placeholder=""
                      options={Object.entries(statusesOptionDict).map(([value, label]) => ({
                        value,
                        label,
                      }))}
                      onChange={(selection: any) => {
                        const values = Array.isArray(selection)
                          ? selection.map((option) => option.value)
                          : [];
                        setTempFilters((prev) => ({
                          ...prev,
                          clearance_statuses: values,
                        }));
                      }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 mx-2">
                    <Form.Label className="text-filter-form-label" htmlFor="certifications">
                      Life Support Certifications
                    </Form.Label>
                    <SelectController
                      menuPortalTarget={
                        document.getElementById('filter-dropdown-portal') as HTMLElement
                      }
                      control={control}
                      name="certifications"
                      placeholder=""
                      options={certificationsOptions}
                      onChange={(selection: any) => {
                        const values = Array.isArray(selection)
                          ? selection.map((option) => option.value)
                          : [];
                        setTempFilters((prev) => ({
                          ...prev,
                          certifications: values,
                        }));
                      }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 mx-2">
                    <Form.Label className="text-filter-form-label" htmlFor="board_certifications">
                      Board Certifications
                    </Form.Label>
                    <SelectController
                      menuPortalTarget={
                        document.getElementById('filter-dropdown-portal') as HTMLElement
                      }
                      control={control}
                      name="board_certifications"
                      placeholder=""
                      options={boardCertificationsOptions}
                      onChange={(selection: any) => {
                        const values = Array.isArray(selection)
                          ? selection.map((option) => option.value)
                          : [];
                        setTempFilters((prev) => ({
                          ...prev,
                          board_certifications: values,
                        }));
                      }}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3 mx-2">
                    <Form.Label className="text-filter-form-label">Available Date Range</Form.Label>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        type="date"
                        value={tempFilters.schedulesStartOnGte}
                        onChange={(e) => {
                          setTempFilters((prev) => ({
                            ...prev,
                            schedulesStartOnGte: e.target.value,
                          }));
                        }}
                      />
                      <span>-</span>
                      <Form.Control
                        type="date"
                        value={tempFilters.schedulesStartOnLte}
                        onChange={(e) => {
                          setTempFilters((prev) => ({
                            ...prev,
                            schedulesStartOnLte: e.target.value,
                          }));
                        }}
                      />
                    </div>
                  </Form.Group>
                </FilterDropdown>
              </div>
            </Col>
            <Col className="ps-1">
              <SelectController
                control={control as Control<Record<string, unknown>>}
                name="order"
                placeholder="Sort by"
                options={sortOptions}
                isMulti={false}
                components={{
                  Control: ({ children, ...props }: ControlProps) => (
                    <components.Control {...props}>
                      <span className="sorting-btn">
                        {getValues().sortDirection === 'asc' ? (
                          <BsSortUp onMouseDown={changeSortDirection} size={16} />
                        ) : (
                          <BsSortDown onMouseDown={changeSortDirection} size={16} />
                        )}
                      </span>
                      {children}
                    </components.Control>
                  ),
                }}
                onChange={(selection: any) => {
                  const sortDirection = getValues().sortDirection;
                  const value = selection?.value;

                  if (value) {
                    setOrderFilter(sortDirection === 'desc' ? `-${value}` : value);
                  } else {
                    setOrderFilter('');
                  }
                }}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <Row className="justify-content-center" xs={12}>
        <Col>
          <Card className="mb-4">
            <div className="table-responsive">
              <table className="table table-striped table-hover align-middle mb-0">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          style={{
                            width: `${header.getSize()}%`,
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="table-group-divider">{renderItems()}</tbody>
              </table>
            </div>
          </Card>
          {(queryProviders.data?.count ?? 0) !== 0 && (
            <div className="hstack gap-3 justify-content-center mb-4">
              <span>
                {queryProviders.data?.count ?? 0}{' '}
                {queryProviders.data?.count === 1 ? 'provider' : 'providers'}
              </span>
              <Pagination
                count={queryProviders.data?.count ?? 0}
                pageSize={PAGE_SIZE}
                currentPage={currentPage}
                siblingCount={2}
                onChangePage={handleChangePage}
              />
            </div>
          )}
        </Col>
      </Row>
    </>
  );
};

const TableRow = ({
  children,
  onClick,
  className,
  ...props
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  'data-testid'?: string;
}) => (
  <tr
    onClick={onClick}
    className={`${className || ''} ${onClick ? 'cursor-pointer' : ''}`}
    {...props}
  >
    {children}
  </tr>
);

const SkeletonTableRows = () => {
  return Array(PAGE_SIZE)
    .fill(0)
    .map((_, index) => (
      <TableRow key={index} data-testid={`skeleton-row-${index}`}>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
      </TableRow>
    ));
};

const Badges = ({ items, providerId }: { items: string[]; providerId: string }) => (
  <div className={`${styles.lineClamp} ${styles.gap}`}>
    {items.map((item) => (
      <span key={`${providerId}-${item}`} className={`badge fw-medium ${styles.badge}`}>
        {item}
      </span>
    ))}
  </div>
);
